import Vue from 'vue';
import { Auth0LockPasswordless } from 'auth0-lock';
import { Auth0UserProfile } from 'auth0-js';
import * as Cookies from 'es-cookie';

const CORP_TO_CONNECTOR: { [key: string]: string } = {
    '-1052653964': 'Okta',
    955049786: 'email',
};

const COOKIE_CORPORATE = 'corpName';
const LOGO_URL = 'https://hqtravel.com/wp-content/uploads/2022/03/Email_HQ.svg';
const DEFAULT_CONNECTION = 'email';
const DEFAULT_CORPNAME = 'default';

let instance: Vue;

/** Returns the current instance of the SDK */
export const getInstance = () => instance;

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
export const useAuth0 = ({ ...options }) => {
    if (instance) return instance;

    // The 'instance' is simply a Vue object
    instance = new Vue({
        data() {
            return {
                loading: true,
                authenticated: false,
                lock: {} as Auth0LockStatic,
                accessToken: '' as string,
                expiresAt: '' as string,
                profile: {} as Auth0UserProfile,
            };
        },

        methods: {
            login(corp: string, allowedConnections: string[] | undefined = undefined) {
                const state = JSON.stringify({ corp });

                this.lock.show({
                    allowedConnections,
                    auth: {
                        params: {
                            state,
                        },
                    },
                });
            },

            showError(msg: string) {
                this.lock.show({
                    allowedConnections: ['error'],
                    flashMessage: {
                        type: 'error',
                        text: msg,
                    },
                });
            },

            isAccessTokenValid() {
                if (!this.expiresAt) return false;
                const expiresAt = JSON.parse(this.expiresAt);
                const valid = new Date().getTime() < expiresAt;
                return valid;
            },

            checkSessionAndRefreshToken(): Promise<boolean> {
                return new Promise((resolve, reject) => {
                    // this is here to handle the first ever login to the new Auth0Lock for users
                    // that already logged in but will have to login again so a cookie of their corp be saved
                    if (!this.getMyCorpName()) { return resolve(false); }

                    this.lock.checkSession({ responseType: 'token' }, (error, authResult) => {
                        if (authResult) {
                            this.saveToken(authResult.accessToken, authResult.expiresIn);
                            resolve(true);
                        } else {
                            resolve(false);
                        }
                    });
                });
            },

            saveToken(accessToken: string, expiresIn: number) {
                this.accessToken = accessToken;
                this.expiresAt = JSON.stringify((expiresIn * 1000) + new Date().getTime());
            },

            getUserInfo() {
                return new Promise((resolve, reject) => {
                    this.lock.getUserInfo(this.accessToken, (error, profileResult) => {
                        if (error) {
                            console.error(error);
                            reject(error);
                        } else {
                            this.profile = profileResult;
                            resolve(profileResult);
                        }
                    });
                });
            },

            getMyCorpName(): string | undefined {
                return Cookies.get(COOKIE_CORPORATE);
            },

            saveCorpName(corp: string) {
                Cookies.set(COOKIE_CORPORATE, corp, { expires: 365 });
            },

            async getTokenSilently() {
                if (!this.isAccessTokenValid()) {
                    await this.checkSessionAndRefreshToken();
                }
                return this.accessToken;
            },

            // params: returnTo to know where should we go after the logout
            logout(params: any) {
                this.lock.logout(params);
            },
        },

        /** Use this lifecycle method to instantiate the SDK client */
        // There are 3 states that 'created' can be in
        // 1. we are already authenticated (refereshed page) - so after checking it with checkSessionAndRefreshToken we save the token and userInfo and go on
        // 2. we are not authenticated - so we set authenticated bool to false and let the AuthGuard handle it and call the login of Auth0
        // 3. we are in the middle of authentication - we just got redirected from the Auth server and got some data in the url - in this case
        // we are not setting the loading bool to false because we have to wait for authentication to finish in the authenticated event
        async created() {
            // Create a new instance of the SDK client using members of the given options object
            this.lock = new Auth0LockPasswordless(options.clientId, options.domain, {
                auth: {
                    audience: options.audience,
                    redirectUrl: window.location.origin,
                    responseType: 'token',
                },
                closable: false,
                allowSignUp: false,
                languageDictionary: {
                    title: 'Business Dashboard',
                    signUpWithLabel: 'Sign In with %s',
                    passwordlessEmailInstructions: 'Enter your email to sign in',
                    signUpTerms: 'By signing In, you agree to our terms of service and privacy policy.',
                    error: {
                        passwordless: {
                            'lock.fallback': 'Couldn\'t find your HQ Shuttles account <br> Please contact support@ridehip.com',
                        },
                    },
                },
                theme: {
                    logo: LOGO_URL,
                    primaryColor: '#5687e1',
                    authButtons: {
                        Okta: {
                            displayName: 'Okta',
                            primaryColor: '#1662dd',
                            icon: 'https://www.okta.com/sites/default/files/Dev_Logo-02_White_Large.png',
                        },
                    },
                    labeledSubmitButton: false,
                },
                avatar: null,
            });

            // after login - save corp name, token and userinfo
            this.lock.on('authenticated', async (authResult) => {
                this.authenticated = true;
                const { corp } = JSON.parse(authResult.state);
                this.saveCorpName(corp);
                this.saveToken(authResult.accessToken, authResult.expiresIn);
                await this.getUserInfo();
                this.loading = false;
            });

            this.lock.on('authorization_error', (error) => {
                console.error(error);
                this.loading = false;
            });

            // in case we just returned from a redirect from the auth server - we are still loading
            // we will finish loading when the authenticated\error event trigger
            if (window.location.search.includes('access_token=') || window.location.hash.includes('access_token=')) {
                // remove the query params of the rediret - timeout is here becuase page is not fully loaded yet
                setTimeout(() => { window.history.replaceState({}, document.title, window.location.pathname); }, 2000);
                return;
            }

            // normal flow - not a redirection from the Auth server
            this.authenticated = await this.checkSessionAndRefreshToken();
            if (this.authenticated) { await this.getUserInfo(); }
            this.loading = false;
        },
    });

    return instance;
};

export const authGuard = (to: any, from: any, next: any) => {
    const authService = getInstance();

    const fn = () => {
        // If the user is authenticated, continue with the route
        if (authService.authenticated) {
            return next();
        }

        ////////////////////
        // Otherwise, log in
        ////////////////////
        let connection = DEFAULT_CONNECTION;
        let corpName = DEFAULT_CORPNAME;

        // is it a special login page?
        const loginMatch = to?.redirectedFrom?.match(/login\/(\w+)/);
        const corpNameInAddress = loginMatch?.[1] as string | null | undefined;
        if (corpNameInAddress) {
            const corpHash = corpNameInAddress.hashCode();
            if (CORP_TO_CONNECTOR[corpHash]) {
                connection = CORP_TO_CONNECTOR[corpHash];
                corpName = corpNameInAddress;
            }

        // is it a reconnecting corp user?
        } else if (authService.getMyCorpName()) {
            const corpNameInCookie = authService.getMyCorpName();
            const corpHash = corpNameInCookie.hashCode();
            if (CORP_TO_CONNECTOR[corpHash]) {
                connection = CORP_TO_CONNECTOR[corpHash];
                corpName = corpNameInCookie;
            }
        }

        // show a login page
        authService.login(corpName, [connection]);
    };

    // If loading has already finished, check our auth state using `fn()`
    if (!authService.loading) {
        return fn();
    }

    // Watch for the loading property to change before we check if authenticated
    authService.$watch('loading', (loading) => {
        if (loading === false) {
            return fn();
        }
    });
};

// Create a simple Vue plugin to expose the wrapper object throughout the application
export default {
    install(V: typeof Vue, options: any) {
        V.prototype.$auth = useAuth0(options);
    },
};
